import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { downloadFile, ServicesURL } from "@utils/utilsApi";
import { getCookie } from "@utils/utilsCookie";
import { axiosInstance } from "src/services/config";

const sessionId = getCookie("sessionId");
const fileMaxSize = 15000000;
const fileMinSize = 10;

export const useTrainingCsvUpload = () => {
  const { t } = useTranslation(["skillBook-page"]);
  const { id } = useParams();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const reset = () => {
    setShowSuccessMessage(false);
    setSelectedFile(undefined);
    setErrorMessage("");
  };

  const selectFile = (file: File | undefined) => {
    if (file) {
      if (!["text/csv"].includes(file.type)) {
        setErrorMessage(
          t("validation.file_type_error", { acceptedFormats: "CSV" })!,
        );
      } else if (file.size < fileMinSize || file.size > fileMaxSize) {
        setErrorMessage(
          t("validation.file_size_error_range", {
            minSize: `${fileMinSize} byte`,
            maxSize: `${(fileMaxSize / 1000000).toFixed(1)} MB`,
          })!,
        );
      } else {
        setErrorMessage("");
        setSelectedFile(file);
      }
    }
  };

  const confirmUpload = () => {
    if (!selectedFile || !id) return;

    setIsLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    axiosInstance
      .post(
        `${ServicesURL.getSkillBook}/bulk-upload?corporateId=${id}`,
        formData,
        {
          headers: {
            "x-ada-session-token": sessionId,
            accept: "text/csv",
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      )
      .then((res) => {
        if (res.status !== 204) {
          setErrorMessage(t("upload_error")!);

          const href = URL.createObjectURL(res.data);
          const fileName = res.headers["content-disposition"]
            .split("filename=")[1]
            .split(".")[0];
          const fileExtension = res.headers["content-disposition"]
            .split(".")[1]
            .split(";")[0];

          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", `${fileName}.${fileExtension}`);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        } else {
          setSelectedFile(undefined);
          setShowSuccessMessage(true);
          setErrorMessage("");
        }
      })
      .catch((error) => {
        console.error("fileUpload", error);

        if (error?.code === "ERR_NETWORK") {
          setErrorMessage(t("completed_upload_with_error")!);
        } else {
          setErrorMessage(t("upload_error_generic")!);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const downloadTemplate = async () => {
    await downloadFile(
      `${ServicesURL.getSkillBook}/${id}/bulk-upload/template`,
      false,
      "",
      {
        headers: {
          "x-ada-session-token": sessionId,
        },
      },
    );
  };

  return {
    selectedFile,
    errorMessage,
    isLoading,
    showSuccessMessage,
    reset,
    selectFile,
    confirmUpload,
    downloadTemplate,
  };
};
